import React, { useState, useRef, useEffect } from 'react';
import { X, Camera, CheckCircle2, Sparkles, RefreshCw, Star, Zap } from 'lucide-react';
import confetti from 'canvas-confetti';

interface QRScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStampUnlocked: (id: number) => void;
  onViewStamp: (id: number) => void;
}

export const QRScannerModal: React.FC<QRScannerModalProps> = ({
  isOpen,
  onClose,
  onStampUnlocked,
  onViewStamp,
}) => {
  const [cameraActive, setCameraActive] = useState<boolean>(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [scanSuccess, setScanSuccess] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  const startCamera = async () => {
    stopCamera();
    setCameraError(null);

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Thiết bị hoặc trình duyệt không hỗ trợ truy cập camera.');
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraActive(true);
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Không thể kết nối máy ảnh';
      setCameraError(errorMsg + '. Bạn vẫn có thể trải nghiệm bằng nút "Quét QR mẫu".');
      setCameraActive(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setScanSuccess(false);
      setIsScanning(false);
      startCamera();
    } else {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
  }, [isOpen]);

  const handleDemoScan = () => {
    setIsScanning(true);
    setTimeout(() => {
      setIsScanning(false);
      setScanSuccess(true);
      onStampUnlocked(2); // Unlock Pho tuong Phat Ba Quan Am (id: 2)

      try {
        confetti({
          particleCount: 100,
          spread: 80,
          origin: { y: 0.6 },
          colors: ['#ffd166', '#ff3366', '#06d6a0', '#8b5cf6'],
        });
      } catch (e) {
        // Confetti fallback
      }
    }, 1200);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-black/85 backdrop-blur-lg animate-fade-in">
      <div 
        className="relative w-full max-w-xl bg-gradient-to-b from-[#281332] to-[#1a0c22] border-2 border-[#ffb703]/60 rounded-3xl shadow-2xl overflow-hidden text-[#fff8ee]"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-[#ffb703]/30 bg-[#2f173b]">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-[#ff3366]/20 border border-[#ff3366]/50 text-[#ff708f] flex items-center justify-center font-black">
              <Camera className="w-5 h-5" />
            </div>
            <div>
              <strong className="text-base sm:text-lg font-black text-white block">
                Ống Kính Săn Tem Bảo Vật 📸
              </strong>
              <span className="text-[11px] text-[#ffd166] font-medium block">
                Quét mã QR tại phòng trưng bày
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-black/50 hover:bg-black/80 text-white border border-[#ffd166]/50 flex items-center justify-center transition-all active:scale-95"
            aria-label="Đóng camera"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        {!scanSuccess ? (
          <div className="p-5 sm:p-6 flex flex-col items-center">
            {/* Viewfinder View */}
            <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden bg-black flex items-center justify-center border-2 border-[#ffd166]/40 shadow-inner">
              <video
                ref={videoRef}
                className={`absolute inset-0 w-full h-full object-cover ${cameraActive ? 'opacity-100' : 'opacity-0'}`}
                playsInline
                muted
                autoPlay
              />

              {!cameraActive && (
                <div className="relative z-10 px-6 text-center text-white/90 max-w-sm">
                  <Camera className="w-12 h-12 mx-auto mb-3 text-[#ffd166] animate-bounce" />
                  <p className="text-sm font-black mb-2 text-white">Khung Ngắm Săn QR</p>
                  <p className="text-xs text-[#e0d0c5]">
                    {cameraError || 'Cho phép máy ảnh để quét mã QR tại các bảng trưng bày hiện vật của Bảo tàng.'}
                  </p>
                </div>
              )}

              {/* Viewfinder Target Frame */}
              <div className="absolute z-20 w-48 h-48 sm:w-56 sm:h-56 border-2 border-[#ffd166] rounded-3xl pointer-events-none flex items-center justify-center shadow-[0_0_0_9999px_rgba(0,0,0,0.65)]">
                <div className="absolute inset-2 border-2 border-dashed border-[#06d6a0]/80 rounded-2xl" />
                {/* Laser animation */}
                <div className="absolute left-4 right-4 h-1 bg-gradient-to-r from-[#ff3366] via-[#ffd166] to-[#06d6a0] rounded-full shadow-[0_0_14px_#ffd166] animate-bounce" />
              </div>
            </div>

            {/* Status note */}
            <p className="mt-4 text-xs text-center text-[#e8d4c5] font-medium">
              {isScanning ? (
                <span className="text-[#ffd166] font-black flex items-center gap-1.5 animate-pulse">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Đang nhận diện mã QR VNFAM-QB-003…
                </span>
              ) : (
                'Hướng khung ngắm vào mã QR dán tại chân bảng trưng bày hiện vật.'
              )}
            </p>

            {/* Actions */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3 w-full">
              <button
                onClick={startCamera}
                className="gf-button gf-button-secondary text-xs sm:text-sm flex-1"
              >
                Khởi động lại camera
              </button>
              <button
                onClick={handleDemoScan}
                disabled={isScanning}
                className="gf-button text-xs sm:text-sm flex-1 flex items-center justify-center gap-1.5"
              >
                <Sparkles className="w-4 h-4" />
                <span>⚡ Quét Thử Nghiệm (QB-003)</span>
              </button>
            </div>
          </div>
        ) : (
          /* Success Screen */
          <div className="p-6 sm:p-8 text-center space-y-5 animate-scale-up">
            <div className="w-18 h-18 sm:w-20 sm:h-20 rounded-3xl bg-gradient-to-br from-[#06d6a0] to-[#059669] text-[#022c22] flex items-center justify-center mx-auto shadow-2xl animate-bounce-wiggle">
              <CheckCircle2 className="w-10 h-10 stroke-[2.5]" />
            </div>

            <div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#06d6a0]/20 border border-[#06d6a0]/50 text-[#06d6a0] text-xs font-black uppercase tracking-wider mb-2">
                <Zap className="w-3.5 h-3.5 fill-current" />
                <span>MỞ KHÓA THÀNH CÔNG · +50 XP</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-black text-white mt-1">
                Thu thập tem thành công! 🎉
              </h3>
              <p className="text-xs sm:text-sm text-[#e8d4c5] mt-1 max-w-sm mx-auto">
                Bạn vừa quét đúng hiện vật <strong>Pho tượng Phật Bà Quan Âm</strong> và mở khóa con tem vào Balo!
              </p>
            </div>

            {/* Stamp Card Highlight */}
            <div className="p-4 rounded-2xl bg-[#200e27] border-2 border-[#ffd166]/50 flex items-center gap-4 text-left max-w-md mx-auto shadow-xl">
              <div className="w-16 h-20 sm:w-20 sm:h-24 bg-black/40 rounded-xl p-1.5 border border-[#ffd166]/40 shrink-0 flex items-center justify-center">
                <img
                  src="/images/generated/stamps-v5/stamp-phat-ba-quan-am-v5.webp"
                  alt="Pho tượng Phật Bà Quan Âm"
                  className="w-full h-full object-contain filter drop-shadow-md"
                />
              </div>
              <div>
                <div className="flex items-center gap-1">
                  <span className="text-[10px] font-black text-[#ffd166] uppercase">
                    QB-003 · THẾ KỶ XVI
                  </span>
                  <span className="text-[10px] text-[#ffd166]">⭐⭐⭐⭐⭐</span>
                </div>
                <h4 className="font-black text-white text-base sm:text-lg">
                  Pho tượng Phật Bà Quan Âm
                </h4>
                <p className="text-xs text-[#06d6a0] font-bold">✓ Đã lưu vào Balo Thợ Săn</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={() => {
                  onClose();
                  onViewStamp(2);
                }}
                className="gf-button text-xs sm:text-sm flex-1"
              >
                <span>🔍 Xem Thẻ Bài Vừa Mở</span>
              </button>
              <button
                onClick={() => {
                  setScanSuccess(false);
                  startCamera();
                }}
                className="gf-button gf-button-secondary text-xs sm:text-sm flex-1"
              >
                <span>Săn tem tiếp theo</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
